import { CommonModule, NgFor } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

import {
  Client,
  ConfigurationProgressStatus,
  GuidDictionaryItem,
  LastCommunicationPeriod,
  MeterStatus,
} from '../../../../../../../api/api-reference';
import { MultiSelectDropdownSearchComponent } from '../../../../multi-select-dropdown-search/multi-select-dropdown-search.component';
import { FilterPanelComponent } from '../../filter-panel.component';

export interface DeviceFilterOutput {
  meterMakes: string[];
  meterModels: string[];
  selectedAssetTypes: string[];
  selectedServiceTypes: string[];
  selectedStatuses: string[];
  selectedMeterOnlineStatus: MeterStatus | undefined;
  selectedSimStatus: MeterStatus | undefined;
  selectedLastCommunication: LastCommunicationPeriod | undefined;
  areas: string[];
  regions: string[];
}

@Component({
  selector: 'app-device-management-filter',
  standalone: true,
  imports: [CommonModule, NgFor, TranslateModule, FilterPanelComponent, MultiSelectDropdownSearchComponent],
  templateUrl: './device-management-filter.component.html',
  styleUrl: './device-management-filter.component.scss',
})
export class DeviceManagementFilterComponent implements OnInit {
  @Input() numberOfResults: number = 0;
  @Input() selectedMeterMakes: string[] = [];
  @Input() selectedMeterModels: string[] = [];
  @Input() selectedAssetTypes: string[] = [];
  @Input() selectedServiceTypes: string[] = [];
  @Input() selectedStatuses: string[] = [];
  @Input() onlineStatus: MeterStatus | undefined = undefined;
  @Input() simStatus: MeterStatus | undefined = undefined;
  @Input() lastCommunication: LastCommunicationPeriod | undefined = undefined;
  @Input() selectedAreas: string[] = [];
  @Input() selectedRegions: string[] = [];

  @Output() applied = new EventEmitter<DeviceFilterOutput>();

  assetTypes: GuidDictionaryItem[] = [];
  serviceTypes: GuidDictionaryItem[] = [];

  readonly configurationStatuses = Object.values(ConfigurationProgressStatus);
  readonly meterStatuses = Object.values(MeterStatus);
  readonly lastCommunicationPeriods = Object.values(LastCommunicationPeriod);

  draftMeterMakes: string[] = [];
  draftMeterModels: string[] = [];
  draftAssetTypes: string[] = [];
  draftServiceTypes: string[] = [];
  draftStatuses: string[] = [];
  draftMeterStatus: MeterStatus | undefined = undefined;
  draftSimStatus: MeterStatus | undefined = undefined;
  draftLastCommunication: LastCommunicationPeriod | undefined = undefined;
  draftAreas: string[] =[];
  draftRegions: string[]=[];

  get activeFilterCount(): number {
    return (
      this.selectedMeterMakes.length +
      this.selectedMeterModels.length +
      this.selectedAssetTypes.length +
      this.selectedServiceTypes.length +
      this.selectedAreas.length +
      this.selectedRegions.length +
      (this.onlineStatus != null ? 1 : 0) +
      (this.simStatus != null ? 1 : 0) +
      (this.lastCommunication != null ? 1 : 0) +
      this.selectedStatuses.length
    );
  }

  fetchMeterMakes = () => this.client.getAllMeterMakersForOptions();
  fetchMeterModels = () => this.client.getAllMeterModelForMeterMakes([]);
  fetchRegions = () => this.client.getAllRegionsForOptions();
  fetchAreas = () => this.client.getAllAreasForOptions();

  constructor(private client: Client, private toastr: ToastrService) {}

  ngOnInit(): void {
    this.reset();
    this.client.getAllAssetTypesForOptions(undefined).subscribe({
      next: (res) => (this.assetTypes = res ?? []),
      error: (err) => this.toastr.error(err),
    });
    this.client.getAllServiceTypesForOptions().subscribe({
      next: (res) => (this.serviceTypes = res ?? []),
      error: (err) => this.toastr.error(err),
    });
  }

  isIn<T>(list: T[], item: T): boolean {
    return list.includes(item);
  }

  toggle<T>(list: T[], item: T): T[] {
    return list.includes(item) ? list.filter((i) => i !== item) : [...list, item];
  }

  toggleGuid(list: string[], item: GuidDictionaryItem): string[] {
    const val = item.value!;
    return list.includes(val) ? list.filter((v) => v !== val) : [...list, val];
  }

  onMeterStatusChange(status: string): void {
    const statusEnum = MeterStatus[status as keyof typeof MeterStatus];
    this.draftMeterStatus = this.draftMeterStatus === statusEnum ? undefined : statusEnum;
  }

  onSimStatusChange(status: string): void {
    const statusEnum = MeterStatus[status as keyof typeof MeterStatus];
    this.draftSimStatus = this.draftSimStatus === statusEnum ? undefined : statusEnum;
  }

  onLastCommunicationChange(period: LastCommunicationPeriod): void {
    this.draftLastCommunication = this.draftLastCommunication === period ? undefined : period;
  }

  onMeterMakeListReceived(list: string[]): void {
    this.draftMeterMakes = list;
  }

  onMeterModelListReceived(list: string[]): void {
    this.draftMeterModels = list;
  }

  onAreaListReceived(list: string[]): void {
    this.draftAreas = list;
  }

  onRegionListReceived(list: string[]): void {
    this.draftRegions = list;
  }

  onApply(): void {
    this.applied.emit({
      meterMakes: this.draftMeterMakes,
      meterModels: this.draftMeterModels,
      selectedAssetTypes: this.draftAssetTypes,
      selectedServiceTypes: this.draftServiceTypes,
      selectedStatuses: this.draftStatuses,
      selectedMeterOnlineStatus: this.draftMeterStatus,
      selectedSimStatus: this.draftSimStatus,
      selectedLastCommunication: this.draftLastCommunication,
      areas: this.draftAreas,
      regions: this.draftRegions
    });
  }

  onCancel(): void {
    this.reset();
  }

  private reset(): void {
    this.draftMeterMakes = [...this.selectedMeterMakes];
    this.draftMeterModels = [...this.selectedMeterModels];
    this.draftAssetTypes = [...this.selectedAssetTypes];
    this.draftServiceTypes = [...this.selectedServiceTypes];
    this.draftStatuses = [...this.selectedStatuses];
    this.draftMeterStatus = this.onlineStatus;
    this.draftSimStatus = this.simStatus;
    this.draftLastCommunication = this.lastCommunication;
    this.draftAreas = [...this.selectedAreas];
    this.draftRegions = [...this.selectedRegions];
  }
}